import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Avatar,
  Link,
  Stack,
  Chip,
  Grid,
  Paper,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
} from "@mui/material";
import {
  AccessTime,
  Timer,
  Replay,
  Videocam,
  PlayCircle,
  InfoOutlined,
  Person,
  Email,
  Phone,
  AddAPhoto,
  Delete,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import axios from "axios";
import CameraPermissionDialog from "../intropages/camera-permission";

const PublicInterviewEvaluation = () => {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showInterviewScreen, setShowInterviewScreen] = useState(false);
  const [open, setOpen] = useState(false);
  const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(true);
  const location = useLocation();
  const [positions, setPositions] = useState([]);
  const [position, setPosition] = useState();
  const [candidate, setCandidate] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User information state
  const [userInfo, setUserInfo] = useState({
    profileImage: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const queryParams = new URLSearchParams(location.search);
  const urlId = queryParams.get("url_id");
  const publicLink = queryParams.get("publicLink");

  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    const fetchQuestionsAndPosition = async () => {
      if (!publicLink) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.get(
          `https://api.a2ajobs.com/api/questions?public_link=${publicLink}`
        );

        if (response.data.status === "success") {
          setPosition(response.data.data);
          setQuestions(response.data.data.questions || []);
          setCandidate(response.data.candidate);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        setError("Error loading interview data");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionsAndPosition();
  }, [publicLink]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if the file is an image
      if (!file.type.match('image.*')) {
        setFormErrors(prev => ({
          ...prev,
          profileImage: "Please select an image file"
        }));
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors(prev => ({
          ...prev,
          profileImage: "Image size should be less than 5MB"
        }));
        return;
      }

      setUserInfo(prev => ({
        ...prev,
        profileImage: file
      }));

      // Clear error if any
      if (formErrors.profileImage) {
        setFormErrors(prev => ({
          ...prev,
          profileImage: ""
        }));
      }
    }
  };

  const handleRemoveImage = () => {
    setUserInfo(prev => ({
      ...prev,
      profileImage: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUserInfoChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!userInfo.profileImage) errors.profileImage = "Profile image is required";
    if (!userInfo.firstName.trim()) errors.firstName = "First name is required";
    if (!userInfo.lastName.trim()) errors.lastName = "Last name is required";
    if (!userInfo.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(userInfo.email)) {
      errors.email = "Email is invalid";
    }
    if (!userInfo.phone.trim()) errors.phone = "Phone number is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUserInfoSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Create FormData to send binary image data
      const formData = new FormData();
      formData.append("first_name", userInfo.firstName);
      formData.append("last_name", userInfo.lastName);
      formData.append("email", userInfo.email);
      formData.append("number", userInfo.phone);
      formData.append("prefix", '+91');
      formData.append("public_link", publicLink);
      formData.append("position_id", position?.id);
      formData.append("url_id", urlId);
      formData.append("is_public", 1);

      // Append the image file if available
      if (userInfo.profileImage) {
        formData.append("image", userInfo.profileImage);
      }

      // Send user information to the API
      const response = await axios.post(
        "https://api.a2ajobs.com/api/candidate-create",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        setUserInfoDialogOpen(false);
        setCandidate(response.data.data.url_id);
      } else {
        setSubmitError("Failed to save your information. Please try again.");
      }
    } catch (error) {
      console.error("Error saving candidate information:", error);
      setSubmitError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getVideoUrl = (url) => {
    if (!url) return null;

    if (url.startsWith('https://') || url.startsWith('http://')) {
      return url;
    }

    if (url.startsWith('/storage/')) {
      return `https://einterview.schoolmgmtsys.com${url}`;
    }

    return null;
  };

  const positionTitle = position?.title || "Default Job Title";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "90vh",
        backgroundColor: "#f8f9fa",
        padding: { md: 0, xs: 1 },
      }}
    >
      {/* User Information Dialog */}
      <Dialog
        open={userInfoDialogOpen}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Welcome to the Interview
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please provide your information to start the interview process.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Stack spacing={2}>
            {/* Profile Image Upload Field */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={fileInputRef}
                style={{ display: 'none' }}
                id="profile-image-upload"
              />
              <label htmlFor="profile-image-upload">
                <IconButton component="span">
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: userInfo.profileImage ? 'transparent' : '#f0f0f0',
                      border: userInfo.profileImage ? 'none' : '2px dashed #ccc',
                    }}
                    src={userInfo.profileImage ? URL.createObjectURL(userInfo.profileImage) : null}
                  >
                    {!userInfo.profileImage && <AddAPhoto sx={{ color: '#7D7D7D' }} />}
                  </Avatar>
                </IconButton>
              </label>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<AddAPhoto />}
                  sx={{ mr: 1 }}
                >
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    hidden
                  />
                </Button>
                {userInfo.profileImage && (
                  <IconButton size="small" onClick={handleRemoveImage}>
                    <Delete />
                  </IconButton>
                )}
              </Box>
              {formErrors.profileImage && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.profileImage}
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="First Name"
              value={userInfo.firstName}
              onChange={(e) => handleUserInfoChange("firstName", e.target.value)}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
              InputProps={{
                startAdornment: <Person sx={{ color: "action.active", mr: 1 }} />
              }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={userInfo.lastName}
              onChange={(e) => handleUserInfoChange("lastName", e.target.value)}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
              InputProps={{
                startAdornment: <Person sx={{ color: "action.active", mr: 1 }} />
              }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={userInfo.email}
              onChange={(e) => handleUserInfoChange("email", e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              InputProps={{
                startAdornment: <Email sx={{ color: "action.active", mr: 1 }} />
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={userInfo.phone}
              onChange={(e) => handleUserInfoChange("phone", e.target.value)}
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              InputProps={{
                startAdornment: <Phone sx={{ color: "action.active", mr: 1 }} />
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleUserInfoSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: "#1666EB",
              textTransform: "none",
              fontSize: "16px",
              px: 3,
            }}
          >
            {loading ? "Processing..." : "Start Interview"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Interview Content (only shown after user info is submitted) */}
      {!userInfoDialogOpen && (
        <>
          {!showInterviewScreen ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "80vh",
                backgroundColor: "#f8f9fa",
                padding: { md: 2, xs: 0 },
              }}
            >
              <Card
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  maxWidth: 900,
                  borderRadius: 3,
                  boxShadow: 3,
                  overflow: "hidden",
                  height: "auto",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "90%", md: "40%" },
                    backgroundColor: "#0E1624",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "#1666EB",
                      fontSize: 32,
                      mb: 2,
                    }}
                    src={userInfo.profileImage ? URL.createObjectURL(userInfo.profileImage) : null}
                  >
                    {!userInfo.profileImage && (userInfo.firstName?.charAt(0)?.toUpperCase() || "J")}
                  </Avatar>

                  <Typography
                    variant="h6"
                    sx={{ color: "white", fontWeight: 600, mb: 1 }}
                  >
                    {`${userInfo.firstName || "John"} ${userInfo.lastName || "Doe"}`}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}
                  >
                    {userInfo.email || "john.doe@example.com"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {userInfo.phone}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", md: "60%" },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    padding: { md: 3, xs: 2 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: "bold", color: "#7D7D7D" }}
                  >
                    VIDEO INTERVIEW
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "800", color: "#1666EB", mt: 1 }}
                  >
                    {positionTitle} - Axcel
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "#7D7D7D" }}>
                    Before starting, watch the intro video to understand the
                    process. Make sure you're in a quiet place with working
                    equipment. You'll see the questions on the right. Start
                    recording once, answer all questions, then stop and press
                    Submit.
                  </Typography>

                  {/* Interview Configuration Cards */}
                  {position && (
                    <Paper
                      elevation={1}
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9"
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, display: "flex", alignItems: "center" }}>
                        Interview Configuration
                        <Tooltip title="These settings define how your interview will be conducted">
                          <InfoOutlined sx={{ fontSize: 16, ml: 0.5, color: "#7D7D7D" }} />
                        </Tooltip>
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6} md={4}>
                          <Tooltip title="Thinking Time: Time allowed to prepare your answer">
                            <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                              <AccessTime sx={{ fontSize: 18, color: "#1666EB", mr: 0.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: "medium" }}>
                                {position.candidate_thinking_time || "15 Seconds"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Tooltip title="Max Answer Length: Maximum duration for each answer">
                            <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                              <Timer sx={{ fontSize: 18, color: "#1666EB", mr: 0.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: "medium" }}>
                                {position.candidate_max_answer_length || "2 Minutes"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Tooltip title="Question Retakes: Number of attempts for each question">
                            <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                              <Replay sx={{ fontSize: 18, color: "#1666EB", mr: 0.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: "medium" }}>
                                {position.question_retakes || "1 Retake"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<PlayCircle />}
                    sx={{
                      mt: 3,
                      bgcolor: "#1666EB",
                      textTransform: "none",
                      fontSize: "16px",
                      mx: 1,
                    }}
                    onClick={() => setShowInterviewScreen(true)}
                  >
                    Watch Intro
                  </Button>
                </Box>
              </Card>
            </Box>
          ) : (
            <Card
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                maxWidth: 900,
                borderRadius: 3,
                boxShadow: 3,
                overflow: "hidden",
                height: { xs: "auto", md: "500px" },
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  backgroundColor: "#0E1624",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  minHeight: { xs: "200px", md: "auto" },
                }}
              >
                {getVideoUrl(position?.intro_video) && (
                  <video
                    src={getVideoUrl(position?.intro_video)}
                    style={{
                      width: "100%",
                      maxWidth: "500px",
                      borderRadius: "8px",
                    }}
                    controls
                    onError={(e) => {
                      console.error("Intro video failed to load:", e);
                      console.error("Video URL:", position?.intro_video);
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  padding: { xs: 2, md: 3 },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "bold",
                    color: "#7D7D7D",
                    fontSize: { xs: "12px", md: "14px" },
                  }}
                >
                  VIDEO INTERVIEW
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "800",
                    color: "#1666EB",
                    mt: 1,
                    fontSize: { xs: "18px", md: "24px" },
                  }}
                >
                  {positionTitle} - Axcel
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: "#7D7D7D",
                    fontSize: { xs: "12px", md: "14px" },
                  }}
                >
                  Welcome to your interview! After watching the introduction, click
                  below to start answering the questions. Ensure your environment is
                  ready.
                </Typography>

                {/* Interview Configuration Chips */}
                {position && (
                  <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Tooltip title="Thinking Time: Time allowed to prepare your answer">
                      <Chip
                        icon={<AccessTime />}
                        label={position.candidate_thinking_time || "15 Seconds"}
                        size="small"
                        variant="outlined"
                        sx={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                    <Tooltip title="Max Answer Length: Maximum duration for each answer">
                      <Chip
                        icon={<Timer />}
                        label={position.candidate_max_answer_length || "2 Minutes"}
                        size="small"
                        variant="outlined"
                        sx={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                    <Tooltip title="Question Retakes: Number of attempts for each question">
                      <Chip
                        icon={<Replay />}
                        label={position.question_retakes || "1 Retake"}
                        size="small"
                        variant="outlined"
                        sx={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </Box>
                )}

                <Stack
                  direction={"row"}
                  gap={2}
                  alignItems={"center"}
                  justifyContent={{ xs: "center", md: "flex-start" }}
                  mt={2}
                >
                  <Button
                    variant="contained"
                    startIcon={<Videocam />}
                    sx={{
                      bgcolor: "#1666EB",
                      textTransform: "none",
                      fontSize: { xs: "14px", md: "16px" },
                      padding: { xs: "6px 12px", md: "8px 16px" },
                    }}
                    onClick={() => setOpen(true)}
                  >
                    Start Interview
                  </Button>
                  <Stack color={"#7D7D7D"}>
                    {questions?.length} questions
                  </Stack>
                </Stack>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 2,
                    color: "#7D7D7D",
                    fontSize: { xs: "10px", md: "12px" },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  By clicking "Start Interview," you agree to our{" "}
                  <Link>Terms and Conditions</Link>
                </Typography>
              </Box>
            </Card>
          )}
        </>
      )}

      <CameraPermissionDialog
        open={open}
        onClose={() => setOpen(false)}
        questions={questions}
        candidate={candidate}
        position={position}
        thinkingTime={position?.candidate_thinking_time}
        maxAnswerLength={position?.candidate_max_answer_length}
      />
    </Box>
  );
};

export default PublicInterviewEvaluation;